// Ressenti du « first bite » des onglets racine et des pages de détail.
//
// Ouvrir un onglet coûte deux allers-retours (shell puis `load=true`) : jusqu'ici l'écran
// ne bougeait pas tant que le shell n'était pas revenu, soit ~0,3 s p50 et jusqu'à ~0,8 s
// p90 de page figée. On peint donc le squelette localement dès le clic, avant même que la
// requête parte, et on pose l'onglet actif de façon optimiste. Une show se paie exactement
// les mêmes deux allers-retours, et le shell y répond justement par un squelette : même
// remède, déclencheur différent (cf. plus bas).
//
// Le squelette n'est pas construit en JS : le serveur pré-rend un <template> par variante
// utilisée par la nav (cf. NavSkeletonTemplatesComponent), et on en clone le contenu.
//
// Deux pièges traités ici :
//   - turbo:before-cache prend le snapshot de la page qu'on QUITTE. L'overlay doit en être
//     retiré avant, sinon un retour arrière réaffiche un squelette figé.
//   - un clic ne garantit pas une navigation (visite annulée, erreur réseau). D'où les
//     retraits sur turbo:load / turbo:render / erreurs, plus un filet de sécurité.
//
// Et un relais à tenir : la page d'arrivée rend le même squelette, qui prend la place de
// celui-ci au changement d'URL. Il en reprend l'animation là où elle en était au lieu de la
// rejouer depuis le début (cf. skeleton_handoff.ts).

import { continueSkeletons, readSkeletonClock } from './skeleton_handoff';

const OVERLAY_CLASS = 'nav-instant-skeleton';
// Un squelette peint par-dessus un autre (deux onglets cliqués d'affilée) : il est déjà en
// place, il ne refait pas son entrée.
const RELAY_CLASS = 'nav-instant-skeleton--relay';
const ACTIVE_CLASSES = ['fw-bold', 'text-primary-emphasis'];
const SAFETY_TIMEOUT_MS = 10_000;

let safetyTimeout: number | null = null;
// Horloge du squelette relevée juste avant qu'il ne parte (turbo:before-cache, qui précède le
// rendu de la page d'arrivée dans la même image) et reprise par celui de cette page.
let handoffClock: number | null = null;
// Une visite Turbo est-elle en cours ? Le filet de sécurité ne doit pas retirer l'overlay
// d'une navigation simplement LENTE : mesuré en local, /dashboard/joint_invoice met 14 s.
// Avec un retrait aveugle à 10 s, l'écran repassait sur l'onglet précédent avant l'arrivée
// de la réponse — d'où « je clique, le squelette s'affiche, puis le DOM revient en arrière ».
let visitInFlight = false;

const mainElement = () =>
  document.querySelector<HTMLElement>('main.app-shell__main') ||
  document.querySelector<HTMLElement>('.main-content-column');

// Le filet ne se déclenche que si PLUS AUCUNE visite n'est en cours : une visite lente doit
// garder son squelette jusqu'au bout. Tant qu'une visite est en vol, on réarme.
const armSafetyTimeout = () => {
  if (safetyTimeout !== null) window.clearTimeout(safetyTimeout);

  safetyTimeout = window.setTimeout(() => {
    safetyTimeout = null;
    if (visitInFlight) {
      armSafetyTimeout();
      return;
    }

    removeSkeleton();
    syncActiveNavLink();
  }, SAFETY_TIMEOUT_MS);
};

const removeSkeleton = () => {
  if (safetyTimeout !== null) {
    window.clearTimeout(safetyTimeout);
    safetyTimeout = null;
  }
  document.querySelectorAll(`.${OVERLAY_CLASS}`).forEach((node) => node.remove());
};

const paintSkeleton = (variant: string) => {
  const template = document.querySelector<HTMLTemplateElement>(
    `template[data-nav-skeleton="${CSS.escape(variant)}"]`
  );
  const main = mainElement();
  if (!template || !main) return;

  const previous = main.querySelector(`.${OVERLAY_CLASS}`);
  const clock = readSkeletonClock(previous);
  removeSkeleton();

  const overlay = document.createElement('div');
  overlay.className = previous ? `${OVERLAY_CLASS} ${RELAY_CLASS}` : OVERLAY_CLASS;
  overlay.setAttribute('aria-hidden', 'true');
  overlay.appendChild(template.content.cloneNode(true));
  main.appendChild(overlay);
  continueSkeletons(overlay, clock);

  armSafetyTimeout();
};

// --- pages de détail --------------------------------------------------------------

const SHOW_VARIANT = 'show';
const WORKFLOW_VARIANT = 'workflow';
const SHOW_VARIANTS = [SHOW_VARIANT, WORKFLOW_VARIANT];

// Une show vaut `<préfixe>/<modèle>/<id>` : c'est la forme des routes de l'engine
// rails_admin, dont le serveur nous donne le préfixe (locale comprise) sur le <template>.
// Le reste des routes tombe de lui-même : un segment pour un index, trois pour l'historique,
// et un fragment non numérique pour les actions de collection (new, export, bulk_delete…).
const SHOW_MODEL_SEGMENT = /^[a-z0-9_~]+$/;
const SHOW_ID_SEGMENT = /^\d+$/;

// Paramètres qui font sortir show.html.erb de la branche « coquille + squelette » : la
// réponse est alors le contenu réel, une section ou l'historique — jamais le squelette
// Informations. `global_workflow` est traité à part, plus bas.
const NON_SKELETON_SHOW_PARAMS = [
  'load',
  'section',
  'history',
  'modal',
  'nested',
  'preview',
  'turbo_request'
];

const showSkeletonTemplate = () =>
  document.querySelector<HTMLTemplateElement>(`template[data-nav-skeleton="${SHOW_VARIANT}"]`);

const isShowPath = (url: URL, prefix: string) => {
  if (!prefix) return false;
  if (url.origin !== window.location.origin) return false;
  if (!url.pathname.startsWith(`${prefix}/`)) return false;

  const segments = url.pathname.slice(prefix.length + 1).split('/');
  return (
    segments.length === 2 && SHOW_MODEL_SEGMENT.test(segments[0]) && SHOW_ID_SEGMENT.test(segments[1])
  );
};

// Quel squelette pour cette visite, ou aucun.
//
// `hinted` vient du lien cliqué : certaines shows redirigent vers un parcours, et seul le
// serveur peut le savoir — un contrat pas encore souscrit, un sinistre encore en brouillon.
// Il n'annonce que les cas décidables sans requête (cf. NavHelper#show_skeleton_data), donc
// l'absence d'indication ne veut pas dire « pas de parcours », juste « on ne sait pas ».
const showSkeletonVariant = (url: URL, prefix: string, hinted: string | null) => {
  if (!isShowPath(url, prefix)) return null;

  // Déjà dans un parcours : show.html.erb rend directement le contenu réel, sans coquille
  // ni squelette. C'est le cas où l'écran reste figé le plus longtemps, et le seul où la
  // variante se lit sur l'URL au lieu de se deviner.
  if (url.searchParams.has('global_workflow')) return WORKFLOW_VARIANT;

  if (NON_SKELETON_SHOW_PARAMS.some((param) => url.searchParams.has(param))) return null;

  return hinted || SHOW_VARIANT;
};

// L'élément cliqué n'est connu qu'à `turbo:click` ; la certitude qu'une visite pleine page
// part vraiment n'arrive qu'à `turbo:before-visit`. On retient donc l'indication entre les
// deux — c'est exactement ce que Turbo fait de son côté pour ses frames.
let pendingShowHint: { url: string; variant: string } | null = null;

const captureShowHint = (link: Element | null, url: string | undefined) => {
  const variant = (link as HTMLElement | null)?.dataset?.showSkeleton;
  pendingShowHint = url && variant && SHOW_VARIANTS.includes(variant) ? { url, variant } : null;
};

const takeShowHint = (url: string | undefined) => {
  const hint = pendingShowHint;
  pendingShowHint = null;
  return hint && hint.url === url ? hint.variant : null;
};

// --- onglet actif -----------------------------------------------------------------

const navLinks = () =>
  Array.from(document.querySelectorAll<HTMLAnchorElement>('a[data-nav-match-paths]'));

// Le lien d'action principale (« Nouveau contrat ») ne s'allume jamais — même exception que
// côté serveur (`@btn_class != 'btn-primary'`). Il participe en revanche au calcul du
// meilleur match : sur /insurance_policy/new son chemin est le préfixe le plus long, donc il
// gagne et AUCUN onglet n'est surligné. L'exclure des candidats faisait remonter Contrats,
// qui s'allumait à tort alors qu'on est sur le formulaire de création.
const isHighlightable = (link: HTMLAnchorElement) => !link.classList.contains('btn-primary');

const matchPaths = (link: HTMLAnchorElement): string[] => {
  try {
    const parsed = JSON.parse(link.dataset.navMatchPaths || '[]');
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

// Même règle que NavLinkComponent#on_current_link_or_nested? : le préfixe le plus long gagne.
// `data-nav-match-exact` : la page elle-même seulement (Accueil de la barre du bas), barre oblique
// finale comprise — `/` redirige vers `/fr/dashboard/`, l'onglet vise `/fr/dashboard`. Le score
// l'ignore aussi : sinon l'Accueil de la sidebar (`/fr/dashboard/`) l'emportait d'un caractère
// sur celui de la barre du bas, qui restait éteint.
const withoutTrailingSlash = (path: string) => (path.length > 1 ? path.replace(/\/+$/, '') : path);

const bestMatchScore = (link: HTMLAnchorElement, pathname: string) =>
  matchPaths(link).reduce((best, path) => {
    if (!path) return best;
    const exact = link.dataset.navMatchExact === 'true';
    const matches = exact
      ? withoutTrailingSlash(pathname) === withoutTrailingSlash(path)
      : pathname === path || pathname.startsWith(`${path}/`);
    const score = withoutTrailingSlash(path).length;
    return matches && score > best ? score : best;
  }, -1);

const setActiveNavLinks = (targets: HTMLAnchorElement[]) => {
  navLinks().forEach((link) => {
    const isActive = targets.includes(link) && isHighlightable(link);
    ACTIVE_CLASSES.forEach((klass) => link.classList.toggle(klass, isActive));
  });
};

// Tous les liens du meilleur score, pas seulement le premier : la sidebar et la barre du bas sur
// téléphone (MobileNavbarComponent) ont chacune leur lien vers la même page. Le premier trouvé,
// celui de la sidebar masquée, gardait seul l'état actif, et l'onglet du bas restait éteint.
const syncActiveNavLink = (pathname = window.location.pathname) => {
  let best: HTMLAnchorElement[] = [];
  let bestScore = -1;

  navLinks().forEach((link) => {
    const score = bestMatchScore(link, pathname);
    if (score > bestScore) {
      bestScore = score;
      best = [link];
    } else if (score === bestScore && score >= 0) {
      best.push(link);
    }
  });

  setActiveNavLinks(bestScore >= 0 ? best : []);
};

// --- navigations sans lien --------------------------------------------------------

// Les points d'un graphe du tableau de bord ne sont pas des liens : une barre, un marqueur ou
// une entrée de légende appelle `Turbo.visit` lui-même (cf. chart_index_filter_url.js). Ni
// `turbo:click` ni le marquage `data-nav-skeleton` ne peuvent donc les atteindre, alors que
// leur destination — un index filtré — est exactement celle d'un onglet de sidebar, avec la
// même attente : deux allers-retours pendant lesquels l'écran ne bougeait pas.
//
// À appeler AVANT de lancer la visite. Rien à annuler ensuite : le squelette est retiré par la
// réconciliation habituelle (`turbo:render` / `turbo:load` / erreur de fetch), et par le filet
// de sécurité si la visite ne part jamais.
export function paintNavSkeletonForVisit(url: string, variant: string) {
  let pathname: string;
  try {
    pathname = new URL(url, window.location.origin).pathname;
  } catch {
    // Une URL illisible ne doit pas emporter la navigation avec elle : le clic doit partir.
    return;
  }

  // Même pari que sur un onglet de sidebar — état actif posé tout de suite — et même
  // rattrapage sur l'URL réelle à l'arrivée si la visite n'aboutit pas.
  syncActiveNavLink(pathname);
  paintSkeleton(variant);
}

// --- câblage ----------------------------------------------------------------------

export function initializeNavInstantSkeleton() {
  if ((window as any).__navInstantSkeletonInitialized) return;
  (window as any).__navInstantSkeletonInitialized = true;

  document.addEventListener('turbo:visit', () => {
    visitInFlight = true;
    // Une horloge relevée pour une visite qui n'a rien rendu ne vaut rien pour celle-ci.
    handoffClock = null;
  });

  const visitSettled = () => {
    visitInFlight = false;
  };
  document.addEventListener('turbo:load', visitSettled);
  document.addEventListener('turbo:render', visitSettled);
  document.addEventListener('turbo:fetch-request-error', visitSettled);

  document.addEventListener('turbo:click', (event) => {
    const target = event.target as Element | null;
    const url = (event as CustomEvent).detail?.url as string | undefined;

    // Retenu pour `turbo:before-visit`, qui ne connaît que l'URL. Fait avant toute sortie :
    // `turbo:click` se déclenche aussi pour les liens qui vont se charger dans une frame, et
    // le retenir sans visite derrière est sans conséquence — il est consommé ou écrasé.
    captureShowHint(target?.closest('[data-show-skeleton]') || null, url);

    const link = target?.closest<HTMLAnchorElement>('a[data-nav-skeleton]');
    if (!link) return;

    // Pas de squelette pour un clic sur l'onglet déjà ouvert : rien ne va changer à l'écran.
    if (url && new URL(url, window.location.origin).pathname === window.location.pathname) return;

    // État actif posé tout de suite, pour que la sidebar réagisse en même temps que le body.
    // C'est un PARI : `turbo:click` ne garantit pas la navigation. Il est systématiquement
    // rattrapé par la réconciliation sur l'URL réelle (turbo:render / turbo:load / popstate,
    // plus le filet de sécurité ci-dessous). Sans ce rattrapage, une visite qui n'aboutit pas
    // laissait l'onglet surligné à tort — c'était le bug du dashboard affiché avec Contrats
    // actif.
    if (url) syncActiveNavLink(new URL(url, window.location.origin).pathname);
    paintSkeleton(link.dataset.navSkeleton as string);
  });

  // `turbo:before-visit` et pas `turbo:click` comme pour la sidebar : Turbo 8 émet
  // `turbo:click` pour TOUS les liens qu'il intercepte, y compris ceux qui vont se charger
  // dans une turbo-frame — le LinkInterceptor d'une frame écoute cet événement-là puis
  // l'annule. Or la moitié des liens vers une show visent une frame (`section`, `nested`,
  // `preview`, `modal`). `turbo:before-visit` ne se déclenche que pour une vraie visite
  // pleine page, et toujours avant que la requête ne parte : rien n'est perdu côté ressenti.
  // Il ne se déclenche pas non plus sur un retour arrière, qui doit afficher le snapshot en
  // cache et non un squelette.
  document.addEventListener('turbo:before-visit', (event) => {
    const url = (event as CustomEvent).detail?.url as string | undefined;
    // Consommée quoi qu'il arrive, y compris sur les sorties anticipées plus bas : une
    // indication laissée en place serait relue par la visite suivante, dont l'URL n'a
    // aucune raison d'être la même.
    const hinted = takeShowHint(url);

    // Visite refusée en amont (lien externe, preventDefault applicatif) : on remet l'écran
    // dans son état réel.
    if (event.defaultPrevented) {
      removeSkeleton();
      syncActiveNavLink();
      return;
    }

    // Une visite est déjà en attente d'écran (onglet de sidebar, show précédente) : son
    // squelette est le bon, ne pas le remplacer.
    if (document.querySelector(`.${OVERLAY_CLASS}`)) return;

    const template = showSkeletonTemplate();
    if (!template || !url) return;

    const prefix = template.dataset.navSkeletonPathPrefix || '';
    const variant = showSkeletonVariant(new URL(url, window.location.origin), prefix, hinted);
    if (variant) paintSkeleton(variant);
  });

  // Réconcilier à chaque rendu, pas seulement à turbo:load : un aperçu depuis le cache ou
  // un rendu tardif change ce qui est à l'écran sans forcément déclencher turbo:load.
  //
  // `turbo:render` part avant le premier paint de la page d'arrivée (Turbo la rend dans des
  // micro-tâches, sans rendre la main au navigateur) : son squelette reprend ici le fil de
  // l'overlay, sans qu'aucune image ne l'ait montré repartir de zéro.
  document.addEventListener('turbo:render', () => {
    removeSkeleton();
    syncActiveNavLink();
    continueSkeletons(mainElement(), handoffClock);
    handoffClock = null;
  });
  window.addEventListener('popstate', () => syncActiveNavLink());

  // Avant que Turbo ne clone la page courante pour son cache — et, sur une visite qui aboutit,
  // dans la même image que le rendu de la page d'arrivée : c'est le dernier moment où l'on peut
  // lire l'horloge de l'overlay.
  document.addEventListener('turbo:before-cache', () => {
    handoffClock = readSkeletonClock(document.querySelector(`.${OVERLAY_CLASS}`));
    removeSkeleton();
  });

  document.addEventListener('turbo:fetch-request-error', () => {
    removeSkeleton();
    syncActiveNavLink();
  });

  // Filet : réconcilie l'onglet actif avec l'URL réelle. Indispensable quand la page est
  // servie depuis le cache de prefetch de Turbo, son HTML ayant été rendu sans état actif
  // (cf. NavLinkComponent#on_current_link_or_nested?, `prefetch_request?`).
  document.addEventListener('turbo:load', () => {
    removeSkeleton();
    syncActiveNavLink();
  });
}
